import { Transaction, coinWithBalance } from "@mysten/sui/transactions";

const CLOCK = "0x6";
const CHAIN = "sui:mainnet";
const ZERO = "0x0000000000000000000000000000000000000000000000000000000000000000";

// Objects created by a settled tx (gRPC effects + objectTypes), as {objectId, objectType}.
export async function createdObjects(client, digest) {
  const r = await client.waitForTransaction({ digest, include: { effects: true, objectTypes: true } });
  const tx = r.Transaction ?? r.FailedTransaction;
  const types = tx?.objectTypes || {};
  const changed = tx?.effects?.changedObjects || [];
  return {
    created: changed.filter((c) => c.idOperation === "Created" && c.outputState !== "PackageWrite")
      .map((c) => ({ objectId: c.objectId, objectType: types[c.objectId] || "" })),
    packageId: changed.find((c) => c.outputState === "PackageWrite")?.objectId || null,
  };
}

const typeArg = (t) => t.slice(t.indexOf("<") + 1, -1);

function leU64(bytes) {
  let n = 0n;
  for (let i = bytes.length - 1; i >= 0; i--) n = (n << 8n) | BigInt(bytes[i]);
  return n;
}

// ---- writes (wallet-signed) -------------------------------------------------

// Transfer `amountMist` SUI to a recipient (split from the gas coin, same tx the user signs).
export async function payFee(signExec, client, { recipient, amountMist }) {
  const tx = new Transaction();
  const [fee] = tx.splitCoins(tx.gas, [tx.pure.u64(BigInt(amountMist))]);
  tx.transferObjects([fee], tx.pure.address(recipient));
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function publishCoin(signExec, client, { modules, dependencies }, sender) {
  const tx = new Transaction();
  const up = tx.publish({ modules, dependencies });
  tx.transferObjects([up], tx.pure.address(sender));
  const res = await signExec({ transaction: tx, chain: CHAIN });
  const { created } = await createdObjects(client, res.digest);
  let treasuryCap, metadata, coinType;
  for (const c of created) {
    const t = c.objectType;
    if (t.includes("::coin::TreasuryCap<")) { treasuryCap = c.objectId; coinType = typeArg(t); }
    else if (t.includes("::coin::CoinMetadata<")) metadata = c.objectId;
  }
  if (!treasuryCap || !coinType) throw new Error("Coin publish did not return a TreasuryCap");
  return { treasuryCap, metadata, coinType, digest: res.digest };
}

export async function createCurve(signExec, client, { pkg, configId, coinType, treasuryCap, metadata, totalSupplyBase, feeRecipient, createFeeMist }, ) {
  const tx = new Transaction();
  // Platform launch fee: split it off the gas coin and send to the platform wallet in the SAME
  // transaction the user signs (real SUI, on top of gas). Skipped only if not configured.
  if (feeRecipient && createFeeMist && BigInt(createFeeMist) > 0n) {
    const [fee] = tx.splitCoins(tx.gas, [tx.pure.u64(BigInt(createFeeMist))]);
    tx.transferObjects([fee], tx.pure.address(feeRecipient));
  }
  tx.moveCall({
    target: `${pkg}::launchpad::create_curve`,
    typeArguments: [coinType],
    arguments: [tx.object(configId), tx.object(treasuryCap), tx.object(metadata), tx.pure.u64(BigInt(totalSupplyBase)), tx.object(CLOCK)],
  });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  const { created } = await createdObjects(client, res.digest);
  const curveId = created.find((c) => c.objectType.includes("::launchpad::BondingCurve<"))?.objectId;
  if (!curveId) throw new Error("create_curve did not return a BondingCurve");
  return { curveId, digest: res.digest };
}

export async function buyOnCurve(signExec, client, { pkg, configId, curveId, coinType, amountMist, minOut }) {
  const tx = new Transaction();
  const [pay] = tx.splitCoins(tx.gas, [tx.pure.u64(BigInt(amountMist))]);
  tx.moveCall({
    target: `${pkg}::launchpad::buy`, typeArguments: [coinType],
    arguments: [tx.object(configId), tx.object(curveId), pay, tx.pure.u64(BigInt(minOut || 0)), tx.object(CLOCK)],
  });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function sellOnCurve(signExec, client, { pkg, configId, curveId, coinType, amountBase, minSuiOut }, sender) {
  if ((await getTokenBalance(client, { owner: sender, coinType })) <= 0n) throw new Error("You have no tokens to sell");
  const tx = new Transaction();
  tx.setSender(sender);
  const sellCoin = tx.add(coinWithBalance({ type: coinType, balance: BigInt(amountBase) }));
  tx.moveCall({
    target: `${pkg}::launchpad::sell`, typeArguments: [coinType],
    arguments: [tx.object(configId), tx.object(curveId), sellCoin, tx.pure.u64(BigInt(minSuiOut || 0)), tx.object(CLOCK)],
  });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function migrateCurve(signExec, client, { pkg, configId, curveId, coinType }) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::migrate`, typeArguments: [coinType], arguments: [tx.object(configId), tx.object(curveId), tx.object(CLOCK)] });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function claimCreatorFees(signExec, client, { pkg, curveId, coinType }) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::claim_creator_fees`, typeArguments: [coinType], arguments: [tx.object(curveId)] });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  // Best-effort confirmation: the tx already executed when signExec resolved. On mobile the
  // extra indexing poll often times out through the RPC proxy — don't let that false-negative
  // turn a successful claim into an error toast.
  try { await client.waitForTransaction({ digest: res.digest }); } catch (e) {}
  return res.digest;
}

// ---- admin (AdminCap-gated) -------------------------------------------------

export async function adminSetPayout(signExec, client, { pkg, adminCapId, configId, platform, migrator }) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::set_payout_addresses`, arguments: [tx.object(adminCapId), tx.object(configId), tx.pure.address(platform), tx.pure.address(migrator)] });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function adminSetPaused(signExec, client, { pkg, adminCapId, configId, paused }) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::set_paused`, arguments: [tx.object(adminCapId), tx.object(configId), tx.pure.bool(paused)] });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function adminSetCurveParams(signExec, client, { pkg, adminCapId, configId, virtualSui, graduationSui }) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::set_curve_params`, arguments: [tx.object(adminCapId), tx.object(configId), tx.pure.u64(BigInt(virtualSui)), tx.pure.u64(BigInt(graduationSui))] });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

// ---- reads (devInspect, no signing) ----------------------------------------

// Read-only Move call (gRPC SimulateTransaction with checks off = the old devInspect).
async function inspect(client, tx, sender) {
  tx.setSender(sender || ZERO);
  const res = await client.simulateTransaction({ transaction: tx, include: { commandResults: true }, checksEnabled: false });
  return (res?.commandResults?.[0]?.returnValues || []).map((v) => v.bcs || new Uint8Array([0]));
}

async function inspectU64s(client, tx, sender, count) {
  const rv = await inspect(client, tx, sender);
  return rv.slice(0, count).map((bytes) => leU64(bytes));
}

export async function getCurveState(client, { pkg, curveId, coinType }, sender) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::get_state`, typeArguments: [coinType], arguments: [tx.object(curveId)] });
  const rv = await inspect(client, tx, sender);
  const u = (i) => leU64(rv[i] || [0]);
  const b = (i) => (rv[i]?.[0] === 1);
  return {
    real_sui_collected: u(0),
    token_reserve: u(1),
    token_supply: u(2),
    graduation_sui: u(3),
    migrated: b(4),
    dev_bought_first: b(5),
    volume_sui: u(6),
  };
}

export async function getBuyQuote(client, { pkg, configId, curveId, coinType, amountMist }, sender) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::get_buy_quote`, typeArguments: [coinType], arguments: [tx.object(configId), tx.object(curveId), tx.pure.u64(BigInt(amountMist))] });
  const [out, fee, creatorFee, platformFee] = await inspectU64s(client, tx, sender, 4);
  return { out, fee, creatorFee, platformFee };
}

export async function getSellQuote(client, { pkg, configId, curveId, coinType, amountBase }, sender) {
  const tx = new Transaction();
  tx.moveCall({ target: `${pkg}::launchpad::get_sell_quote`, typeArguments: [coinType], arguments: [tx.object(configId), tx.object(curveId), tx.pure.u64(BigInt(amountBase))] });
  const [out, fee, creatorFee, platformFee] = await inspectU64s(client, tx, sender, 4);
  return { out, fee, creatorFee, platformFee };
}

export async function getTokenBalance(client, { owner, coinType }) {
  try {
    const { balance } = await client.getBalance({ owner, coinType });
    return BigInt(balance.balance);
  } catch (e) { return 0n; }
}

export async function lockTokens(signExec, client, { vestingPkg, coinType, amountBase, days, isDev }, sender) {
  if ((await getTokenBalance(client, { owner: sender, coinType })) <= 0n) throw new Error("No tokens to lock");
  const tx = new Transaction();
  tx.setSender(sender);
  const lockCoin = tx.add(coinWithBalance({ type: coinType, balance: BigInt(amountBase) }));
  tx.moveCall({
    target: `${vestingPkg}::locker::lock`,
    typeArguments: [coinType],
    arguments: [lockCoin, tx.pure.u64(BigInt(days)), tx.pure.bool(!!isDev), tx.object(CLOCK)],
  });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}

export async function unlockTokens(signExec, client, { vestingPkg, coinType, lockId }) {
  const tx = new Transaction();
  tx.moveCall({
    target: `${vestingPkg}::locker::unlock`,
    typeArguments: [coinType],
    arguments: [tx.object(lockId), tx.object(CLOCK)],
  });
  const res = await signExec({ transaction: tx, chain: CHAIN });
  await client.waitForTransaction({ digest: res.digest });
  return res.digest;
}
